import { TFunction } from 'i18next';
import {
  ReoccurrenceType,
  ReportSettingsFieldType,
  ReportSettingsFiltersType,
  PredefinedDateRangeType,
  ReportSettingsType,
  TransformedReportSettingsConfig,
  TransformedReportSettings,
  TransformedFilterSettings,
  PredefinedReports,
} from '../types';
import { getACAHoursWorked, getIntervalTypeOptions } from '../../labor/utilities/laborUtilities';
import { overtimeThresholds } from '../../labor/ACAThresholdReport/ACAThresholdReportDashboard';
import { DateComparisonRanges } from '../../components/GlobalFilterBar/types';
import { yesterday } from '../../utils/reportingUtils/filterUtils';

const createTranslatedOptions = (t: TFunction, optionKeys: readonly string[], translationPrefix: string) =>
  optionKeys.map((key) => ({
    id: key,
    label: t(`${translationPrefix}.${key}`),
    value: key,
  }));

const handleDateRangeFilter = (
  t: TFunction,
  filter: ReportSettingsFiltersType,
  reoccurrence?: string | null
): ReportSettingsFiltersType => {
  if (!reoccurrence || reoccurrence === ReoccurrenceType.NO_REPEAT) {
    return filter;
  }

  const predefinedDateRangeOptions = createTranslatedOptions(
    t,
    Object.values(PredefinedDateRangeType),
    'sales.dateRanges'
  );

  return {
    ...filter,
    type: ReportSettingsFieldType.AUTOCOMPLETE,
    options: predefinedDateRangeOptions,
  };
};

const handleComparedDateRangeFilter = (
  t: TFunction,
  filter: ReportSettingsFiltersType,
  reoccurrence?: string | null
): ReportSettingsFiltersType => {
  const dateComparisonRanges = [
    DateComparisonRanges.PREVIOUS_PERIOD,
    DateComparisonRanges.SAME_DATE_LAST_YEAR,
    DateComparisonRanges.SAME_DAY_LAST_YEAR,
  ];
  if (!reoccurrence || reoccurrence === ReoccurrenceType.NO_REPEAT) {
    return {
      ...filter,
      comparisonRanges: dateComparisonRanges,
    };
  }
  const predefinedCompareRangeOptions = createTranslatedOptions(t, dateComparisonRanges, 'sales.dateRanges');
  return {
    ...filter,
    type: ReportSettingsFieldType.AUTOCOMPLETE,
    options: predefinedCompareRangeOptions,
  };
};

const handleHourlyThresholdsFilter = (t: TFunction, filter: ReportSettingsFiltersType): ReportSettingsFiltersType => {
  const { hoursWorkedOptions } = getACAHoursWorked(t, overtimeThresholds, false);
  return {
    ...filter,
    options: hoursWorkedOptions,
  };
};

const handleGranularityFilter = (t: TFunction, filter: ReportSettingsFiltersType): ReportSettingsFiltersType => {
  const { intervalTypeOptions } = getIntervalTypeOptions(t);
  return {
    ...filter,
    options: intervalTypeOptions,
  };
};

const handleTransactionContains = (t: TFunction, filter: ReportSettingsFiltersType): ReportSettingsFiltersType => {
  const transactionContainsOptions = ['Voids', 'Comps', 'Promos', 'Refunds'];
  const predefinedTransactionContainsOptions = createTranslatedOptions(
    t,
    transactionContainsOptions,
    'sales.transactionDetailsDashboard'
  );
  return {
    ...filter,
    options: predefinedTransactionContainsOptions,
  };
};

export const FILTER_HANDLERS: Record<
  string,
  (t: TFunction, filter: ReportSettingsFiltersType, reoccurrence?: string | null) => ReportSettingsFiltersType
> = {
  dateRange: handleDateRangeFilter,
  comparedDateRange: handleComparedDateRangeFilter,
  hourlyThresholds: handleHourlyThresholdsFilter,
  granularity: handleGranularityFilter,
  transactionContains: handleTransactionContains,
};

export const getFilterProps = (
  t: TFunction,
  filter: ReportSettingsFiltersType,
  reoccurrence: string | null
): ReportSettingsFiltersType => {
  const handler = FILTER_HANDLERS[filter.columnName];
  if (!handler) {
    return filter;
  }

  return handler(t, filter, reoccurrence);
};

export const getScheduleSettingsFormDefaultValues = () => ({
  scheduleName: '',
  report: '',
  reoccurrence: '',
  description: '',
  recipients: [],
  recipientSecurity: false,
  startDate: null,
  runTime: null,
  timezone: '',
  endDate: null,
  noEndDate: false,
  repeatEvery: 1,
  repeatUnit: 'days',
});

export const getReportSettingsFormDefaultValues = (
  reoccurrence: string | null,
  reportSettings: ReportSettingsType[] | null,
  selectedSubReportSettingId: string | null
) => {
  let defaultValues: Record<string, unknown> = {};
  if (reportSettings && selectedSubReportSettingId) {
    const reportFieldConfig = reportSettings.find((reportSetting) => reportSetting.id === selectedSubReportSettingId);
    defaultValues = { ...(reportFieldConfig?.defaultValue || {}) };
  }

  if (reoccurrence && reoccurrence !== ReoccurrenceType.NO_REPEAT) {
    if ('dateRange' in defaultValues) defaultValues.dateRange = null;
    if ('comparedDateRange' in defaultValues) defaultValues.comparedDateRange = null;
  }

  return defaultValues;
};

const transformFilterSettings = (
  sourceFilter: ReportSettingsFiltersType,
  filterIndex: number
): TransformedFilterSettings => {
  const transformedFilter: TransformedFilterSettings = {
    id: filterIndex + 1,
    columnName: sourceFilter.field ?? '',
    required: sourceFilter.isRequired ?? false,
    type: sourceFilter.type,
  };

  // Handle date range filter configuration
  if (sourceFilter.type === 'dateRange') {
    const dateRangeChips = sourceFilter.dateRangeChips ?? [];
    if (dateRangeChips.length > 0) {
      transformedFilter.useChips = true;
      transformedFilter.dateRangeChips = sourceFilter.dateRangeChips;
    } else {
      transformedFilter.isPredefinedDateRangeSelector = true;
    }
  }

  // Handle query filter type
  if (sourceFilter.queryFilterType) {
    transformedFilter.queryFilterType = sourceFilter.queryFilterType;
  }

  // Handle date range comparison defaults
  if (sourceFilter.type === 'dateRangeCompare' && sourceFilter.defaultComparisonRange !== undefined) {
    transformedFilter.defaultComparisonRange = sourceFilter.defaultComparisonRange;
  }

  return transformedFilter;
};

const transformReportDefinition = (
  reportDefinitionWrapper: Record<string, unknown>,
  reportTypeName: string
): TransformedReportSettings | null => {
  const subReportKey = Object.keys(reportDefinitionWrapper)[0];
  const reportDefinition = reportDefinitionWrapper[subReportKey] as {
    filters: ReportSettingsFiltersType[];
    defaultValue?: Record<string, unknown>;
  };
  const transformedFilters = reportDefinition.filters.map(transformFilterSettings);
  return {
    id: subReportKey,
    category: reportTypeName,
    filters: transformedFilters,
    defaultValue: (reportDefinition.defaultValue ?? {}) as Record<string, unknown>,
  };
};

export const getReportConfigurationSettings = (
  reportSettingsConfigResponse: PredefinedReports | null,
  customReportId = ''
): TransformedReportSettingsConfig => {
  if (!reportSettingsConfigResponse) {
    return {};
  }

  const transformedReportConfig: TransformedReportSettingsConfig = {};

  Object.entries(reportSettingsConfigResponse.reportHeaders).forEach(([reportTypeName, reportDefinitionsArray]) => {
    const transformedReportsList = reportDefinitionsArray
      .map((reportDefinitionWrapper) => transformReportDefinition(reportDefinitionWrapper, reportTypeName))
      .filter((report): report is TransformedReportSettings => report !== null);

    transformedReportConfig[reportTypeName] = transformedReportsList;
  });
  if (customReportId) {
    transformedReportConfig[customReportId] = [
      {
        id: customReportId,
        category: customReportId,
        filters: [
          {
            id: 1,
            columnName: 'sites',
            required: true,
            type: ReportSettingsFieldType.SITE_SELECT,
          },
          {
            id: 2,
            columnName: 'dateRange',
            required: true,
            type: ReportSettingsFieldType.DATE_RANGE,
            isPredefinedDateRangeSelector: true,
          },
        ],
        defaultValue: {
          sites: [],
          dateRange: {
            endDate: yesterday,
            startDate: yesterday,
          },
        },
      },
    ];
  }
  return transformedReportConfig;
};

export const MENU_PROPS = {
  MenuProps: {
    disablePortal: false,
    PaperProps: {
      style: {
        maxHeight: 300,
      },
    },
  },
};

export const getDestinationUrlEndpoint = (reportSettingId: string): string => {
  const reportsettingsEndpointMap = reportSettingId ? reportSettingId.replaceAll('_', '-') : '';
  const endpointMap: Record<string, string> = {
    'sales-summary': 'sales-summary/sales-grid',
    'transaction-summary': 'guest-checks',
    'payments-report': 'payments/grid',
    'employee-tip-report': 'employee-tip/grid-data',
    'employee-payroll-details': 'employee-payroll/detail/grid-data',
    'employee-payroll-summary': 'employee-payroll/summary/grid-data',
    'product-mix-menu-items': 'productmix/grid-data',
    'product-mix-employee-sales': 'productmix/employee-sales-grid-data',
    'generic-payroll-report': 'generic-payroll-report',
  };

  return endpointMap[reportsettingsEndpointMap] || reportsettingsEndpointMap;
};
